import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import useTable from '../../../../components/useTable';
import TelusSettingCell from './TelusSettingCell';
import basicServiceHandler from '../../../../services/basicServiceHandler';
import { convertAxiosErrorToUserFacingMessage } from '../../../../context/ApiClientContext';

const EDITABLE_FIELDS = [
  'authCredentials',
  'calendars',
  'providerIds',
  'intakePlanId',
  'appointmentCutoffDateTime',
  'operatingHours',
  'timezone',
  'smsHoursBeforeAppointment',
  'appointmentTypes',
  'excludeNoTypeAppointments',
];

const headers = [
  { id: 'setting', label: 'Setting' },
  { id: 'value', label: 'Value' },
  { id: 'action', label: 'Action' },
  { id: 'reset', label: 'Reset' },
];

const fieldLabels = {
  authCredentials: 'Authentication Credentials',
  calendars: 'Calendars',
  providerIds: 'Provider IDs',
  intakePlanId: 'Intake Plan ID',
  appointmentCutoffDateTime: 'Appointment Cutoff Date/Time',
  operatingHours: 'Operating Hours',
  timezone: 'Timezone',
  smsHoursBeforeAppointment: 'SMS Hours Before Appointment',
  appointmentTypes: 'Appointment types',
  excludeNoTypeAppointments: 'Exclude Appointments Without Type',
};

export default function TelusSettingTable(props) {
  const {
    entity,
    telusSettings,
    handleSave,
    refreshParent,
    setSuccessMessage,
    setErrorMessage,
    setValues,
    apiClient,
  } = props;

  const editableSettings = Object.entries(telusSettings).filter(
    ([key, setting]) => EDITABLE_FIELDS.includes(key) && setting && setting.value !== undefined,
  );

  // Check if authCredentials is properly set with all required fields
  let isAuthCredentialsSet = false;
  if (telusSettings.authCredentials?.value) {
    try {
      const credentials = JSON.parse(telusSettings.authCredentials.value);
      isAuthCredentialsSet = !!(
        credentials.userToken
        && credentials.hardwareIdentifier
        && credentials.userId
        && credentials.password
        && credentials.appToken
        && credentials.pssInstance
      );
    } catch {
      isAuthCredentialsSet = false;
    }
  }
  const [selectedPlans, setSelectedPlans] = useState([]);

  const {
    TblContainer,
    TblHead,
  } = useTable(editableSettings, headers, { fn: (items) => items });

  if (editableSettings.length === 0) {
    return (
      <Paper sx={{ padding: '20px', marginTop: '20px' }}>
        <Typography variant="body1" color="textSecondary">
          No Telus settings configured. Please add settings to manage Telus integration.
        </Typography>
      </Paper>
    );
  }

  const getSelectedPlans = (intakePlanList) => {
    if (!telusSettings.intakePlans) {
      return [];
    }
    const availableIntakePlans = telusSettings.intakePlans.value;
    if (availableIntakePlans === '*') {
      return intakePlanList;
    }
    if (availableIntakePlans === '') {
      return [];
    }
    return intakePlanList.filter((intakePlan) => availableIntakePlans.split(',').includes(intakePlan.id.toString()));
  };

  async function getIntakePlans() {
    basicServiceHandler(
      () => apiClient.getIntakePlans(),
      (response) => {
        const plans = getSelectedPlans(response.data);
        setSelectedPlans(plans);
      },
      (error) => {
        setErrorMessage(convertAxiosErrorToUserFacingMessage(error));
        return [];
      },
    );
  }

  useEffect(() => {
    if (telusSettings.intakePlans && telusSettings.intakePlans.value) {
      getIntakePlans();
    }
  }, []);

  return (
    <TblContainer>
      <TblHead />
      <TableBody>
        {editableSettings.map(([key, setting]) => {
          const { isDefault, ...settingValues } = setting;
          return (
            <TableRow key={key}>
              <TableCell>{fieldLabels[key] || key}</TableCell>
              <TelusSettingCell
                entity={entity}
                setting={settingValues}
                handleSave={handleSave}
                setValues={setValues}
                refreshParent={refreshParent}
                setSuccessMessage={setSuccessMessage}
                setErrorMessage={setErrorMessage}
                apiClient={apiClient}
                selectedPlans={selectedPlans}
                isAuthCredentialsSet={isAuthCredentialsSet}
              />
            </TableRow>
          );
        })}
      </TableBody>
    </TblContainer>
  );
}

// First, define a reusable setting shape
const settingShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

TelusSettingTable.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    settings: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      category: PropTypes.string,
      value: PropTypes.string,
    })),
  }).isRequired,
  handleSave: PropTypes.func.isRequired,
  telusSettings: PropTypes.shape({
    enabled: settingShape,
    authCredentials: settingShape,
    calendars: settingShape,
    providerIds: settingShape,
    intakePlanId: settingShape,
    appointmentCutoffDateTime: settingShape,
    operatingHours: settingShape,
    timezone: settingShape,
    smsHoursBeforeAppointment: settingShape,
    excludeNoTypeAppointments: settingShape,
    intakePlans: settingShape,
  }).isRequired,
  apiClient: PropTypes.shape({
    getIntakePlans: PropTypes.func,
  }).isRequired,
  setValues: PropTypes.func.isRequired,
  refreshParent: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
};
